// SystemLogTable.tsx
import React, { ChangeEvent, useMemo } from 'react';
import { ColumnDef, Row } from '@tanstack/react-table';
import AdvanceTable from 'components/base/AdvanceTable';
import AdvanceTableFooter from 'components/base/AdvanceTableFooter';
import SearchBox from 'components/common/SearchBox';
import FilterTab from 'components/common/FilterTab';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import { SystemLog } from 'services/accountlog.service';
import { format } from 'date-fns';
import Badge from 'components/base/Badge';

interface TableProps {
  logs: SystemLog[];
}

export const SystemLogTable: React.FC<TableProps> = ({ logs }) => {
  const columns: ColumnDef<SystemLog>[] = [
    {
      accessorKey: 'logdate',
      header: 'Date',
      cell: ({ row: { original } }: { row: Row<SystemLog> }) => {
        const date = new Date(original.logdate);
        return <span>{format(date, 'MMM dd, yyyy - HH:mm:ss')}</span>;
      },
      meta: {
        headerProps: { style: { width: '12%' }, className: 'pe-2 ps-0 fs-10' },
        cellProps: { className: 'py-0 pe-2 ps-0 white-space-nowrap' }
      }
    },
    {
      accessorKey: 'logtype',
      header: 'Type',
      cell: ({ row: { original } }: { row: Row<SystemLog> }) => {
        return (
          <Badge
            variant="phoenix"
            bg={
              original.logtype === 'System'
                ? 'secondary'
                : original.logtype === 'QAProcessor'
                ? 'info'
                : 'success'
            }
          >
            {original.logtype}
          </Badge>
        );
      },
      filterFn: (
        row: Row<SystemLog>,
        columnId: string,
        filterValue: string
      ) => {
        if (!filterValue || filterValue === 'all') return true;
        return row.getValue(columnId) === filterValue;
      },
      meta: {
        headerProps: { style: { width: '7%' }, className: 'px-1 fs-10' },
        cellProps: { className: 'px-1 white-space-nowrap' }
      }
    },
    {
      accessorKey: 'title',
      header: 'Title',
      cell: ({ row: { original } }: { row: Row<SystemLog> }) => original.title,
      meta: {
        headerProps: { style: { width: '10%' }, className: 'px-1 fs-10' },
        cellProps: { className: 'px-1 white-space-nowrap' }
      }
    },
    {
      accessorKey: 'username',
      header: 'User',
      cell: ({ row: { original } }: { row: Row<SystemLog> }) =>
        original.username,
      meta: {
        headerProps: { style: { width: '15%' }, className: 'px-1 fs-10' },
        cellProps: { className: 'px-1 white-space-nowrap' }
      }
    },
    {
      accessorKey: 'description',
      header: 'Description',
      cell: ({ row: { original } }: { row: Row<SystemLog> }) =>
        original.description,
      meta: {
        headerProps: { style: { width: '56%' }, className: 'px-1 fs-10' },
        cellProps: { className: 'px-1' }
      }
    }
  ];

  const table = useAdvanceTable({
    data: logs,
    columns,
    pageSize: 18,
    pagination: true,
    sortable: true,
    selection: false
  });

  const handleFilterItemClick = useMemo(
    () => (columnId: string, value: string) => {
      const column = table.getColumn(columnId);
      column?.setFilterValue(value === 'all' ? '' : value);
    },
    [table]
  );

  const tabItems = useMemo(() => {
    const getDataCount = (type: string) =>
      logs.filter(log => log.logtype === type).length;

    return [
      {
        label: 'All',
        value: 'all',
        onClick: () => handleFilterItemClick('logtype', 'all'),
        count: logs.length
      },
      {
        label: 'System',
        value: 'System',
        onClick: () => handleFilterItemClick('logtype', 'System'),
        count: getDataCount('System')
      },
      {
        label: 'Operations',
        value: 'Operations',
        onClick: () => handleFilterItemClick('logtype', 'Operations'),
        count: getDataCount('Operations')
      },
      {
        label: 'QAProcessor',
        value: 'QAProcessor',
        onClick: () => handleFilterItemClick('logtype', 'QAProcessor'),
        count: getDataCount('QAProcessor')
      }
    ];
  }, [logs, handleFilterItemClick]);

  const handleSearchInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    table.setGlobalFilter(e.target.value || undefined);
  };

  return (
    <AdvanceTableProvider {...table}>
      <div className="mb-2 pt-3">
        <div className="d-flex flex-wrap gap-3">
          <SearchBox
            placeholder="Search logs..."
            onChange={handleSearchInputChange}
          />
          <FilterTab tabItems={tabItems} className="pt-3" />
        </div>
      </div>
      <div className="mx-n4 px-4 mx-lg-n6 px-lg-6 border-translucent position-relative top-1 pb-3">
        <AdvanceTable
          tableProps={{
            size: 'sm',
            className: 'phoenix-table fs-9'
          }}
          rowClassName="hover-actions-trigger btn-reveal-trigger position-static"
        />
        <AdvanceTableFooter pagination />
      </div>
    </AdvanceTableProvider>
  );
};
